import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, ArrowLeft, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import { apiService } from '../../services/apiService';
import { formatDateShort, formatSignedPct } from '../../utils/format';
import { pnlColor } from '../../utils/pnl';
import {
  BENCHMARKS, BENCHMARK_DASH, BENCHMARK_SYMBOLS, WindowReturn, seriesOf, windowReturn,
} from '../../utils/benchmarks';
import AgentDetailModal from './AgentDetailModal';
import BenchmarkLegendItem from './BenchmarkLegendItem';
import Scoreboard from './Scoreboard';

interface Agent {
  id: string;
  name: string;
  emoji: string;
  description: string;
  personality: string;
  tier: string;
  risk_level: string;
  current_capital: number;
  starting_capital: number;
  tags: string[];
  archetype?: string;
  inspired_by?: string;
}

interface Performance {
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  win_rate: number;
  total_pnl: number;
  total_pnl_pct: number;
  total_return_pct?: number;
  nav?: number;
  open_positions: number;
  rank: number;
  best_trade_pnl: number;
  worst_trade_pnl: number;
  period_return_pct?: number;
  period_pnl?: number;
}

interface AgentWithPerformance {
  agent: Agent;
  performance: Performance;
}

interface AgentsPageProps {
  onBack: () => void;
}

// Agent line colors — distinct, colorblind-friendly
export const AGENT_COLORS: Record<string, string> = {
  'catalyst-cody': '#22d3ee',     // cyan
  'iron-condor-irene': '#f472b6', // pink
  'theta-mechanic': '#fb923c',    // orange
  'trend-surfer': '#34d399',      // emerald
  'volatility-viper': '#a78bfa',  // violet
  'wheel-walker': '#60a5fa',      // blue
};

const PERIOD_API_MAP: Record<string, string | undefined> = {
  'ALL': undefined, '1W': '7d', '1M': '30d', '3M': '90d', 'YTD': 'YTD', '1Y': '1Y',
};

const PERIOD_LABELS: Record<string, string> = {
  'ALL': 'All-Time', '1W': '1 Week', '1M': '1 Month', '3M': '3 Months', 'YTD': 'Year-to-Date', '1Y': '1 Year',
};

export const AgentsPage: React.FC<AgentsPageProps> = ({ onBack }) => {
  const [agents, setAgents] = useState<AgentWithPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>('ALL');
  // Each benchmark's return over the chart's window, with whether its last close is pending (B-050).
  const [benchmarks, setBenchmarks] = useState<Record<string, WindowReturn>>({});
  // Provenance of the benchmark series (wave 2.2): the basis the returns are computed on and
  // whether the newest close is still provisional. A reader comparing an agent with SPY is
  // entitled to know both; before this the dashboard showed a comparison against a close that
  // was not final, on a basis nobody stated.
  const [benchMeta, setBenchMeta] = useState<Record<string, any>>({});
  // B-050: benchmarks the route could not serve (its `_failed`, or all five when the request failed)
  const [benchFailed, setBenchFailed] = useState<string[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartAgentIds, setChartAgentIds] = useState<string[]>([]);

  // B-059 (Oct 12, 2026): each fetch takes a number and only the latest may apply its answer, so a slow
  // older answer (a quick period switch) can never overwrite a newer one.
  const fetchSeq = useRef(0);

  // B-059: what a fetch shows for its period. A period change clears it all when its fetch starts, so no
  // period's numbers stay on screen under another period's header, in flight or after a failure.
  const clearPeriodData = () => {
    setAgents([]);
    setBenchmarks({});
    setBenchFailed([]);
    setBenchMeta({});
    setChartData([]);
    setChartAgentIds([]);
  };

  const fetchAgents = async (newPeriod = false) => {
    const seq = ++fetchSeq.current;
    try {
      // B-059 (Product, review LOW-1): a period change clears the previous period's numbers when its
      // fetch starts, so they never sit under the new header while the request is in flight (it has no
      // timeout). The chart area shows a spinner instead. Only a period change: Retry runs from the error
      // banner, when there is no old period's data left to clear.
      if (newPeriod) clearPeriodData();
      // The page's first fetch is its initial load (`loading` starts true); every later one is a refresh
      // that keeps the header and the period buttons. Not keyed on `agents`: this closure's copy can be
      // stale, and a cleared page then fell back to the full-page spinner on a quick second switch.
      if (seq > 1) setRefreshing(true);
      setError(null);
      const apiPeriod = PERIOD_API_MAP[period];
      const benchFromDate = (period === 'ALL' || !apiPeriod) ? '2026-05-01' : undefined;

      const [data, benchData] = await Promise.all([
        apiService.getAgentLeaderboard(20, apiPeriod),
        apiService.getBenchmarks(apiPeriod || 'ALL', BENCHMARK_SYMBOLS, benchFromDate)
          .catch(() => ({ _failed: BENCHMARK_SYMBOLS })),
      ]);
      if (seq !== fetchSeq.current) return;   // a newer fetch owns the page now

      // B-057 (Oct 12, 2026): an empty leaderboard, from a transient failure or an empty answer, is
      // shown as an error to retry. It never reseeds: /agents/init rewrites every agent's entry and
      // exit rules with the starter defaults, and this page used to call it on any empty answer.
      const answered: any[] = Array.isArray(data) ? data : [];
      // B-059: a row without an agent id or a performance object (null, {}) crashed the render and blanked
      // the page. It is skipped, with one warning per fetch; the valid rows still render.
      const rows: AgentWithPerformance[] = answered.filter((r) =>
        typeof r?.agent?.id === 'string' && r.agent.id !== '' && !!r.performance
        && typeof r.performance === 'object');
      if (rows.length < answered.length) {
        console.warn(`AgentsPage: skipped ${answered.length - rows.length} malformed leaderboard row(s) `
          + '(no agent id or no performance)');
      }
      if (rows.length === 0) {
        setError('No agents came back from the leaderboard. Nothing was changed; retry in a moment.');
      }
      setAgents(rows);

      // Each benchmark's return over the served window (its first close to its last)
      const benchReturns: Record<string, WindowReturn> = {};
      for (const { symbol } of BENCHMARKS) {
        const r = windowReturn(seriesOf(benchData, symbol));
        if (r) benchReturns[symbol] = r;
      }
      setBenchmarks(benchReturns);
      const failed = (benchData as any)?._failed;
      setBenchFailed(Array.isArray(failed) ? failed : []);

      // Fetch NAV history for all agents to build the hero chart
      const agentIds = rows.map(d => d.agent.id);
      const navPromises = agentIds.map(id =>
        apiService.getAgentNavHistory(id, apiPeriod || 'ALL').catch(() => ({ history: [] }))
      );
      const navResults = await Promise.all(navPromises);
      if (seq !== fetchSeq.current) return;

      // Build unified chart data: union of all dates
      const allDates = new Set<string>();
      const agentNavByDate: Record<string, Record<string, number>> = {};

      agentIds.forEach((id, idx) => {
        const history = navResults[idx].history || [];
        if (history.length < 2) return;
        const baseNav = history[0].nav;
        agentNavByDate[id] = {};
        for (const point of history) {
          allDates.add(point.date);
          agentNavByDate[id][point.date] = ((point.nav - baseNav) / baseNav) * 100;
        }
      });

      // Add benchmark dates
      setBenchMeta(((benchData as any)?._meta as Record<string, any>) || {});
      const benchByDate: Record<string, Record<string, number>> = {};
      for (const { symbol } of BENCHMARKS) {
        for (const bp of seriesOf(benchData, symbol)) {
          if (typeof bp.return_pct !== 'number') continue;
          allDates.add(bp.date);
          if (!benchByDate[bp.date]) benchByDate[bp.date] = {};
          benchByDate[bp.date][symbol] = bp.return_pct;
        }
      }

      // Build merged rows with forward-fill
      const sortedDates = Array.from(allDates).sort();
      const merged: any[] = [];
      const lastValues: Record<string, number> = {};
      const activeAgentIds: string[] = [];

      for (const id of agentIds) {
        if (agentNavByDate[id] && Object.keys(agentNavByDate[id]).length >= 2) {
          activeAgentIds.push(id);
        }
      }

      for (const date of sortedDates) {
        const row: any = { date };

        for (const id of activeAgentIds) {
          if (agentNavByDate[id]?.[date] !== undefined) {
            lastValues[id] = agentNavByDate[id][date];
          }
          if (lastValues[id] !== undefined) {
            row[id] = parseFloat(lastValues[id].toFixed(4));
          }
        }

        // A benchmark is carried across days it has no close, never past its last close (B-050: during the
        // day the funds' last stored close is yesterday's; a flat line into today would read "unchanged")
        for (const { symbol } of BENCHMARKS) {
          if (benchByDate[date]?.[symbol] !== undefined) lastValues[symbol] = benchByDate[date][symbol];
          if (lastValues[symbol] !== undefined && date <= (benchReturns[symbol]?.end ?? '')) {
            row[symbol] = parseFloat(lastValues[symbol].toFixed(4));
          }
        }

        merged.push(row);
      }

      setChartData(merged);
      setChartAgentIds(activeAgentIds);
    } catch (err: any) {
      if (seq !== fetchSeq.current) return;
      // Nothing to clear here: a period change cleared the old period's data when it started, and a
      // Retry fails over its own period's data.
      setError(err.message || 'Failed to load agents');
    } finally {
      if (seq === fetchSeq.current) {   // a stale fetch never ends the newer one's spinner
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchAgents(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading AI Agents...</p>
        </div>
      </div>
    );
  }

  // Helpers
  const agentName = (id: string) => agents.find(a => a.agent.id === id)?.agent.name || id;
  const agentEmoji = (id: string) => agents.find(a => a.agent.id === id)?.agent.emoji || '🤖';
  const hasChartData = chartData.length >= 2;
  // A line for each benchmark with a window return (two closes or more); the legend names all five
  const shownBenchmarks = BENCHMARKS.filter((b) => benchmarks[b.symbol] !== undefined);
  const chartEnd: string | undefined = chartData[chartData.length - 1]?.date;

  return (
    <div className="min-h-[100dvh] bg-slate-900 pb-20">
      {/* Sticky Header */}
      <div className="bg-slate-800/80 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={onBack} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-slate-400" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  AI Fund Performance
                </h1>
                <p className="text-10 text-slate-500 mt-0.5">
                  {PERIOD_LABELS[period]} · {agents.length} funds vs {BENCHMARK_SYMBOLS.slice(0, -1).join(', ')}
                  {' & '}{BENCHMARK_SYMBOLS[BENCHMARK_SYMBOLS.length - 1]}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {refreshing && <RefreshCw className="w-3.5 h-3.5 animate-spin text-cyan-400" />}
              <div className="flex gap-0.5 bg-slate-900/60 p-1 rounded-lg">
                {['ALL', '1W', '1M', '3M', 'YTD', '1Y'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`text-10 px-2.5 py-1.5 rounded-md font-medium transition-colors ${
                      period === p
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
            {error}
            <button onClick={() => fetchAgents()} className="ml-4 underline">Retry</button>
          </div>
        </div>
      )}

      <div className={`max-w-7xl mx-auto px-4 transition-opacity duration-200 ${refreshing ? 'opacity-60' : 'opacity-100'}`}>
        {/* ==================== HERO CHART ==================== */}
        <div className="mt-4 bg-slate-800/50 rounded-xl border border-slate-700/30 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-sm font-bold text-white">Return % vs Market</h2>
            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
              {BENCHMARKS.map((b) => (
                <BenchmarkLegendItem key={b.symbol} benchmark={b} value={benchmarks[b.symbol] ?? null}
                  failed={benchFailed.includes(b.symbol)} chartEnd={chartEnd} />
              ))}
              {(() => {
                // From the drawn benchmarks only: the route's JSON lists its keys alphabetically, so the
                // first entry is JEPI's, and a fund that is not drawn must not name the basis of the lines
                const metas = shownBenchmarks.map((b) => benchMeta?.[b.symbol]).filter(Boolean) as any[];
                if (!metas.length) return null;
                const bases = Array.from(new Set(metas.map((m) => m?.basis || 'basis unstated')));
                const provisional = metas.some((m) => (m?.provisional || []).length > 0);
                const staleBasis = metas.some((m) => m?.stale_basis);
                const label = bases.length > 1 ? 'mixed basis'
                  : bases[0] === 'total_return' ? 'total return' : bases[0];
                return (
                  <span
                    className="text-9 text-slate-500"
                    title={
                      'Benchmark series: ' + label +
                      (provisional ? '; the latest close is provisional until the vendor confirms it' : '') +
                      (staleBasis ? '; the stored series is not on the required basis yet' : '')
                    }
                  >
                    {label}
                    {provisional ? ' · latest close provisional' : ''}
                  </span>
                );
              })()}
            </div>
          </div>

          {hasChartData ? (
            <div>
              <div className="h-72 md:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDateShort}
                      stroke="#64748b"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={40}
                    />
                    <YAxis
                      tickFormatter={(v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`}
                      stroke="#64748b"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      width={52}
                    />
                    <ReferenceLine y={0} stroke="#475569" strokeDasharray="2 2" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        fontSize: '11px',
                        padding: '8px 12px',
                      }}
                      labelFormatter={(label: any) => formatDateShort(String(label))}
                      formatter={(value: any, name: any) => {
                        const bench = BENCHMARKS.find((b) => b.symbol === name);
                        if (bench) return [formatSignedPct(value), bench.label];
                        const agent = agents.find(a => a.agent.id === name);
                        const label = agent ? `${agent.agent.emoji} ${agent.agent.name}` : name;
                        return [formatSignedPct(value), label];
                      }}
                      itemSorter={(item: any) => -(item.value || 0)}
                    />
                    {/* Agent lines — solid */}
                    {chartAgentIds.map((id) => (
                      <Line
                        key={id}
                        type="monotone"
                        dataKey={id}
                        stroke={AGENT_COLORS[id] || '#94a3b8'}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
                    {/* Benchmarks — dashed: SPY, QQQ, JEPI, QYLD, YMAX (B-050) */}
                    {shownBenchmarks.map((b) => (
                      <Line key={b.symbol} type="monotone" dataKey={b.symbol} stroke={b.color} strokeWidth={1.5}
                        strokeDasharray={BENCHMARK_DASH} dot={false} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Chart Legend */}
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-3">
                {chartAgentIds.map((id) => {
                  const last = chartData[chartData.length - 1]?.[id];
                  return (
                    <button
                      key={id}
                      onClick={() => setSelectedAgent(id)}
                      className="flex items-center gap-1.5 text-10 hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <span className="w-3 h-[3px] rounded-sm inline-block" style={{ backgroundColor: AGENT_COLORS[id] || '#94a3b8' }} />
                      <span className="text-slate-300">{agentEmoji(id)} {agentName(id)}</span>
                      {last !== undefined && (
                        <span className={pnlColor(last)}>{formatSignedPct(last)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : refreshing ? (
            <div className="h-56 flex flex-col items-center justify-center" data-testid="period-loading">
              <RefreshCw className="w-6 h-6 animate-spin text-cyan-400 mb-2" />
              <p className="text-xs text-slate-500">Loading {PERIOD_LABELS[period]}…</p>
            </div>
          ) : (
            <div className="h-56 flex flex-col items-center justify-center">
              <BarChart3 className="w-8 h-8 text-slate-600 mb-2" />
              <p className="text-xs text-slate-500">Not enough data for chart</p>
            </div>
          )}
        </div>

        {/* ==================== SCOREBOARD (B-015) ==================== */}
        <div className="mt-5">
          <Scoreboard />
        </div>

        {/* ==================== AGENT CARDS ==================== */}
        <div className="mt-5 space-y-2">
          {agents.map(({ agent, performance }, index) => {
            const rank = index + 1;
            const medalEmoji = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;
            const hasPeriodData = period !== 'ALL' && performance.period_pnl !== undefined && performance.period_pnl !== 0;
            const pnlReturn = hasPeriodData
              ? (performance.period_return_pct ?? 0)
              : (performance.total_return_pct ?? performance.total_pnl_pct ?? 0);
            const pnlPositive = pnlReturn >= 0;
            const beatsSPY = benchmarks.SPY !== undefined && pnlReturn > benchmarks.SPY.pct;
            const beatsQQQ = benchmarks.QQQ !== undefined && pnlReturn > benchmarks.QQQ.pct;
            const color = AGENT_COLORS[agent.id] || '#94a3b8';

            return (
              <div
                key={agent.id}
                onClick={() => setSelectedAgent(agent.id)}
                className={`
                  flex items-center gap-4 px-4 py-3.5 rounded-xl cursor-pointer transition-all
                  ${rank <= 3
                    ? 'bg-slate-800/70 border border-slate-600/40 hover:bg-slate-700/70'
                    : 'bg-slate-800/40 border border-slate-700/30 hover:bg-slate-700/50'
                  }
                `}
              >
                {/* Rank + Color bar */}
                <div className="flex items-center gap-2 w-8 shrink-0">
                  <div className="w-1 h-8 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-xs font-bold text-slate-500">#{rank}</span>
                </div>

                {/* Agent Info */}
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className="relative shrink-0">
                    <span className="text-xl">{agent.emoji}</span>
                    {medalEmoji && <span className="absolute -top-1 -right-1 text-10">{medalEmoji}</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-white text-sm truncate">{agent.name}</div>
                    {agent.archetype && (
                      <div className="text-10 text-cyan-400/60 truncate">{agent.archetype}</div>
                    )}
                  </div>
                </div>

                {/* Return */}
                <div className="text-right shrink-0 w-28">
                  <div className={`font-bold tabular-nums text-sm ${pnlPositive ? 'text-green-400' : 'text-red-400'}`}>
                    {pnlPositive ? '+' : '-'}{Math.abs(pnlReturn).toFixed(2)}%
                  </div>
                  <div className="text-10 md:text-9 text-slate-500 -mt-0.5">NAV Return</div>
                  {!hasPeriodData && (() => {
                    const realizedPct = performance.total_pnl_pct ?? 0;
                    const realizedPositive = realizedPct >= 0;
                    return (
                      <div className={`text-11 font-semibold tabular-nums mt-0.5 ${realizedPositive ? 'text-green-400/80' : 'text-red-400/80'}`}>
                        {realizedPositive ? '+' : '-'}{Math.abs(realizedPct).toFixed(2)}% <span className="text-10 md:text-9 text-slate-500 font-normal">P&L</span>
                      </div>
                    );
                  })()}
                </div>

                {/* Beat indicators */}
                <div className="flex flex-col gap-0.5 shrink-0">
                  {benchmarks.SPY !== undefined && (
                    <span className={`text-10 md:text-9 px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                      beatsSPY ? 'bg-green-500/15 text-green-400' : 'bg-red-500/10 text-red-400/60'
                    }`}>
                      {beatsSPY ? '▲' : '▼'}SPY
                    </span>
                  )}
                  {benchmarks.QQQ !== undefined && (
                    <span className={`text-10 md:text-9 px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                      beatsQQQ ? 'bg-green-500/15 text-green-400' : 'bg-red-500/10 text-red-400/60'
                    }`}>
                      {beatsQQQ ? '▲' : '▼'}QQQ
                    </span>
                  )}
                </div>

                {/* Stats — hidden on mobile */}
                <div className="hidden md:flex items-center gap-6 shrink-0">
                  <div className="text-center w-16">
                    {/* B-059 review: win_rate can come back null; "—", never a crash or a made-up 0 */}
                    <div className="text-xs font-semibold text-white">
                      {Number.isFinite(performance.win_rate) ? `${performance.win_rate.toFixed(0)}%` : '—'}
                    </div>
                    <div className="text-10 md:text-9 text-slate-500">Win Rate</div>
                  </div>
                  <div className="text-center w-16">
                    <div className="text-xs font-semibold text-white">{performance.total_trades}</div>
                    <div className="text-10 md:text-9 text-slate-500">{performance.open_positions} open</div>
                  </div>
                  <div className="text-center w-20">
                    <div className="text-xs font-semibold text-white">
                      ${((performance.nav ?? agent.current_capital) / 1000).toFixed(1)}k
                    </div>
                    <div className="text-10 md:text-9 text-slate-500">NAV</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Agent Detail Modal */}
      {selectedAgent && (
        <AgentDetailModal
          agentId={selectedAgent}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </div>
  );
};

export default AgentsPage;
